/**
 * Transaction rules editor with live preview (§13.3).
 *
 * Why the preview is the feature, not a nicety: these rules decide the headline
 * financial figure. A regex you cannot try is a regex you will get wrong, and
 * getting it wrong here silently moves a number the user is meant to trust.
 * Typing `PAYMENT` as a cashback rule against the real data matches 36 rows
 * worth over four lakh — and you can see that *before* saving, which is the
 * entire point.
 *
 * Matches show their direction, because only credits become Real Cashback (§9).
 * A pattern that catches the right words on the wrong side of the ledger looks
 * like a success until you wonder why the total didn't move.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Badge, Button, Field, Input, Panel, SELECT_CLASS } from "@/components/ui/primitives";
import { rulesApi, type TxnFlag, type TxnRule } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";

const FLAGS: { value: TxnFlag; label: string; help: string }[] = [
  { value: "cashback", label: "Cashback", help: "A credit that reduced a bill as a reward." },
  { value: "payment", label: "Payment", help: "You paying the bill." },
  { value: "fee", label: "Fee", help: "Annual fees, late fees, surcharges." },
  { value: "interest", label: "Interest", help: "Finance charges." },
  { value: "emi", label: "EMI", help: "Instalment conversions." },
  {
    value: "exclude_from_spend",
    label: "Not spend",
    help: "Refunds and reversals — excluded from the effective-rate denominator.",
  },
];

const FLAG_LABEL = Object.fromEntries(FLAGS.map((f) => [f.value, f.label]));

export function RulesPanel() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [flagFilter, setFlagFilter] = useState<TxnFlag | "all">("all");

  const { data: rules, isLoading } = useQuery({ queryKey: ["rules"], queryFn: rulesApi.list });

  const remove = useMutation({
    mutationFn: (id: string) => rulesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rules"] });
      qc.invalidateQueries({ queryKey: ["cashback"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
      toast.success("Rule deleted. Flags and the ledger were re-derived.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      rulesApi.update(id, { active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rules"] });
      qc.invalidateQueries({ queryKey: ["cashback"] });
      toast.success("Rule updated. Flags and the ledger were re-derived.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const visible = (rules ?? []).filter(
    (r) => flagFilter === "all" || r.sets_flag === flagFilter,
  );

  return (
    <Panel className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 p-5">
        <div>
          <h2 className="font-display text-base font-medium text-ink">Rules</h2>
          <p className="mt-1 max-w-prose text-sm text-muted">
            Patterns that decide what a transaction <em>is</em> — cashback, a payment, a fee.
            They drive the Real Cashback total, so try one before you save it.
          </p>
        </div>
        <Button variant="default" size="sm" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add rule
        </Button>
      </div>

      {adding && <RuleForm onDone={() => setAdding(false)} />}

      <div className="flex flex-wrap gap-1.5 border-t border-hairline px-5 py-3">
        {(["all", ...FLAGS.map((f) => f.value)] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFlagFilter(f)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
              flagFilter === f
                ? "border-brass/50 bg-brass/10 text-brass"
                : "border-hairline text-muted hover:text-ink",
            )}
          >
            {f === "all" ? "All" : FLAG_LABEL[f]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2 p-5 pt-0">
          <div className="skeleton h-9 w-full" />
          <div className="skeleton h-9 w-full" />
        </div>
      ) : (
        <ul className="divide-y divide-hairline border-t border-hairline">
          {visible.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              onToggle={() => toggle.mutate({ id: rule.id, active: !rule.active })}
              onDelete={() => remove.mutate(rule.id)}
            />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function RuleRow({
  rule,
  onToggle,
  onDelete,
}: {
  rule: TxnRule;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <li className={cn("flex items-center gap-3 px-5 py-2.5", !rule.active && "opacity-50")}>
      <code className="min-w-0 flex-1 truncate font-mono text-xs text-ink" title={rule.pattern}>
        {rule.pattern}
      </code>
      <Badge tone={rule.sets_flag === "cashback" ? "brass" : "neutral"}>
        {FLAG_LABEL[rule.sets_flag] ?? rule.sets_flag}
      </Badge>
      {rule.seeded && (
        <span className="hidden text-micro uppercase tracking-wider text-faint sm:inline">
          default
        </span>
      )}
      <button
        type="button"
        onClick={onToggle}
        className="rounded px-2 py-1 text-xs text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50"
        title={rule.active ? "Turn this rule off" : "Turn this rule on"}
      >
        {rule.active ? "On" : "Off"}
      </button>
      <Button variant="ghost" size="icon" onClick={onDelete} aria-label="Delete rule">
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </li>
  );
}

function RuleForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [pattern, setPattern] = useState("");
  const [flag, setFlag] = useState<TxnFlag>("cashback");
  const [debounced, setDebounced] = useState("");
  const [error, setError] = useState<string>();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(pattern), 300);
    return () => clearTimeout(t);
  }, [pattern]);

  const { data: preview } = useQuery({
    queryKey: ["rules", "preview", debounced, flag],
    queryFn: () => rulesApi.preview(debounced, flag),
    enabled: debounced.trim().length > 0,
  });

  const create = useMutation({
    mutationFn: () => rulesApi.create({ pattern, sets_flag: flag }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rules"] });
      qc.invalidateQueries({ queryKey: ["cashback"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
      toast.success("Rule saved. Flags and the ledger were re-derived.");
      onDone();
    },
    onError: (e: Error) => setError(e.message),
  });

  // Flagging something as cashback that isn't a credit can't become Real
  // Cashback (§9), so say so rather than letting the user wonder later.
  const creditMatches = preview?.matches.filter((m) => m.direction === "credit").length ?? 0;
  const wouldCount = flag !== "cashback" || creditMatches > 0;

  return (
    <div className="space-y-4 border-t border-hairline bg-base/40 p-5">
      <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
        <Field
          label="Pattern"
          hint="A regular expression matched against the transaction description, case-insensitive."
        >
          <Input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="\bCASHBACK\b"
            autoFocus
            className="font-mono"
          />
        </Field>

        <Field label="Sets flag">
          <select
            value={flag}
            onChange={(e) => setFlag(e.target.value as TxnFlag)}
            className={cn(SELECT_CLASS, "w-full")}
          >
            {FLAGS.map((f) => (
              <option key={f.value} value={f.value} title={f.help}>
                {f.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* --- Live preview ------------------------------------------------- */}
      {debounced.trim() && preview && (
        <div className="rounded-lg border border-hairline bg-panel">
          <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-4 py-2.5">
            {!preview.valid ? (
              <span className="flex items-center gap-1.5 text-xs text-overdue">
                <X className="h-3.5 w-3.5" aria-hidden />
                {preview.error}
              </span>
            ) : (
              <>
                <span
                  className={cn(
                    "flex items-center gap-1.5 text-xs",
                    preview.match_count === 0 ? "text-warn" : "text-paid",
                  )}
                >
                  {preview.match_count === 0 ? (
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <Check className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {preview.explanation}
                </span>
                {preview.total_amount && preview.match_count > 0 && (
                  <span className="ml-auto text-xs tabular-nums text-muted">
                    {formatMoney(preview.total_amount)} total
                  </span>
                )}
              </>
            )}
          </div>

          {preview.valid && preview.matches.length > 0 && (
            <>
              <ul className="max-h-56 divide-y divide-hairline overflow-y-auto">
                {preview.matches.map((m) => (
                  <li
                    key={m.transaction_id}
                    className="flex items-center gap-2 px-4 py-1.5 text-xs"
                  >
                    <span className="w-20 shrink-0 tabular-nums text-faint">
                      {formatDate(m.txn_date)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-ink" title={m.description}>
                      {m.description}
                    </span>
                    <span className="w-24 shrink-0 truncate text-faint">{m.card_name}</span>
                    <span
                      className={cn(
                        "w-16 shrink-0 text-right uppercase tracking-wider",
                        m.direction === "credit" ? "text-paid" : "text-faint",
                      )}
                    >
                      {m.direction}
                    </span>
                    <span className="w-24 shrink-0 text-right tabular-nums text-ink">
                      {formatMoney(m.amount)}
                    </span>
                    {m.already_flagged && (
                      <span className="shrink-0 text-micro uppercase text-faint">already</span>
                    )}
                  </li>
                ))}
              </ul>
              {preview.match_count > preview.matches.length && (
                <p className="border-t border-hairline px-4 py-2 text-xs text-faint">
                  Showing {preview.matches.length} of {preview.match_count} matches.
                </p>
              )}
            </>
          )}

          {preview.valid && flag === "cashback" && preview.match_count > 0 && !wouldCount && (
            <p className="border-t border-hairline px-4 py-2 text-xs text-warn">
              None of these are credits, so none would count as Real Cashback — only
              credit-direction statement rows do.
            </p>
          )}
        </div>
      )}

      {error && <p className="text-xs text-overdue">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          disabled={!pattern.trim() || create.isPending || preview?.valid === false}
          onClick={() => {
            setError(undefined);
            create.mutate();
          }}
        >
          {create.isPending ? "Saving…" : "Save rule"}
        </Button>
      </div>
    </div>
  );
}
